'use client';

import { useMemo, useState } from 'react';

import { Spacer, SpacerSize, TitleMarkup } from '@bytel/trilogy-react';

import { ShortListContext } from '@app-types/eligibility';
import { CategoryCodeInfo } from '@app-types/plan';

import { Faq } from '@components/faq';
import { SSIBlock } from '@components/ssi-block';
import { TabItem, Tabs } from '@components/tabs';

import { PAGE_SLUG, partials } from '@helpers/partials';

import { TECHNOLOGY } from '@constants/provider';

import { EligibilityResults, JourneyConfigType, normalizeTechnology } from '@eligibility/helpers';

import { OfferList } from './offer-list';

const tabsOrder: string[] = [TECHNOLOGY.FTTH, TECHNOLOGY.BOX_5G, TECHNOLOGY.BOX_4G];

function getXGBoxLabel(results: EligibilityResults): string {
    let label = '4G box';
    const offers = results?.offers?.BOX_4G;
    if (!!offers) {
        const has4GBox = offers.some((o) => o.technology === TECHNOLOGY.BOX_4G);
        const has5GBox = offers.some((o) => o.technology === TECHNOLOGY.BOX_5G);
        if (has5GBox) {
            label = '5G box';
            if (has4GBox) {
                label = '4G / 5G box';
            }
        }
    }
    return label;
}

function getTechnologyName(technology: TECHNOLOGY, results: EligibilityResults): string | undefined {
    const technologyName: { [key in TECHNOLOGY]?: string } = {
        [TECHNOLOGY.FTTH]: 'Fibre',
        [TECHNOLOGY.BOX_4G]: getXGBoxLabel(results),
    };

    return technologyName[technology];
}

interface EligibilityResultTabsProps {
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
    shortList?: ShortListContext;
    categoryCodeInfo: CategoryCodeInfo[];
    onAddToCart: (gencode: string) => void;
}

export function EligibilityResultTabs({
    results,
    journeyConfig,
    shortList,
    categoryCodeInfo,
    onAddToCart,
}: Readonly<EligibilityResultTabsProps>) {
    const isPackAvailable = useMemo(
        () => journeyConfig.showPackTabs && !!results.offers?.FTTH,
        [journeyConfig, results],
    );

    const [tabs, defaultTab] = useMemo(() => {
        const technologies = Object.keys(results.offers)
            .map((technology) => {
                return { name: technology, label: getTechnologyName(technology as TECHNOLOGY, results)! };
            })
            .filter((technology) => !!technology.label)
            .sort((a, b) => tabsOrder.indexOf(a.name) - tabsOrder.indexOf(b.name));

        if (isPackAvailable) {
            technologies.push({ name: 'PACK', label: 'Pack' });
        }

        const shortListTechnology = normalizeTechnology(shortList?.technology);

        const shortListTab = technologies.find((t) => t.name === shortListTechnology);

        const defaultTab = shortListTab ?? technologies.find((t) => t.name === results.technology) ?? technologies[0];

        if (shortListTab && shortList) {
            defaultTab!.name = shortList.technology.toUpperCase() as TECHNOLOGY;
        }

        return [technologies, defaultTab];
    }, [results, journeyConfig, shortList, isPackAvailable]);

    const [currentTab, setCurrentTab] = useState<TabItem | undefined>(defaultTab);

    const onTabChange = (activeTab: TabItem) => {
        setCurrentTab(activeTab);
    };

    return (
        <>
            {!shortList?.focus && tabs.length > 1 && <Tabs items={tabs} onClick={onTabChange} activeTab={currentTab} />}
            {currentTab?.name === 'PACK' ? (
                <SSIBlock tagId='pack-cards' url={partials.MARKETING.ELIGIBILITY_PACK} />
            ) : (
                <OfferList
                    currentTab={currentTab as { name: TECHNOLOGY; label: string }}
                    journeyConfig={journeyConfig}
                    results={results}
                    shortList={shortList}
                    categoryCodeInfo={categoryCodeInfo}
                    onAddToCart={onAddToCart}
                />
            )}
            {currentTab?.name === TECHNOLOGY.FTTH && (
                <SSIBlock tagId='post-elig-emerch' url={partials.MARKETING.ELIGIBILITY_FTTH} />
            )}
            <Spacer size={SpacerSize.TEN} />
            <Faq
                id='faq-offres-internet'
                titleMarkup={TitleMarkup.H3}
                title='Vos questions fréquentes sur l’éligibilité à la fibre'
                url={PAGE_SLUG.ELIGIBILITY}
            />
        </>
    );
}
